import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from 'react';
import { BrowseEventTrackingProvider, TrackableDiscoveryItem, DiscoveryItemTypeOptions } from '@udemy-v2/react-browse-event-tracking';
import { LoadingStatus, RecommendationItem, RecommendationSourceObjectType, RecommendationsProvider, useRecommendationsSections } from '@udemy-v2/react-recommendations';
import { StudentsAlsoBought } from './students-also-bought.react-component';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { BrowseCourse } from '@udemy-v2/browse-course';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';
import { useI18n } from '@udemy-v2/i18n';

// This is the maximum number of courses that will be shown in the Students Also Bought section.
const RECOMMENDATIONS_ITEM_COUNT_PER_UNIT = 18;

/**
 * This wrapper is used to wrap the StudentsAlsoBought component with the RecommendationsProvider by passing the correct course.id value and contextId.
 * For UB CLPs, we use the 'org-landing-page' context to ensure that we load "Students also took"-style recommendations for organization users
 */
export const StudentsAlsoBoughtRecommendationsWrapper = () => {
  const $ = _c(14);
  const hasOrganization = useUserHasOrganization();
  const course = useClpCourse();
  const contextId = hasOrganization ? "org-landing-page" : "landing-page";
  let t0;
  if ($[0] !== course.id) {
    t0 = [{
      id: course.id,
      type: RecommendationSourceObjectType.Course
    }];
    $[0] = course.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== contextId || $[3] !== t0) {
    t1 = {
      contextId,
      sourceObjects: t0,
      requestedItemCountPerUnit: RECOMMENDATIONS_ITEM_COUNT_PER_UNIT
    };
    $[2] = contextId;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const contextInput = t1;
  let t2;
  if ($[5] !== course.id) {
    t2 = {
      relatedSourceId: course.id,
      relatedSourceType: DiscoveryItemTypeOptions.COURSE
    };
    $[5] = course.id;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <StudentsAlsoBoughtWrapper />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2) {
    t4 = <BrowseEventTrackingProvider browseEventTrackingContext={t2}>{t3}</BrowseEventTrackingProvider>;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== contextId || $[11] !== contextInput || $[12] !== t4) {
    t5 = <RecommendationsProvider key={contextId} contextInput={contextInput} includeAvailableSubscriptionInfo={true}>{t4}</RecommendationsProvider>;
    $[10] = contextId;
    $[11] = contextInput;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};

/**
 * This wrapper is used to wrap the StudentsAlsoBought component with the logic needed to consume recos data from the RecommendationsProvider.
 */
export const StudentsAlsoBoughtWrapper = () => {
  const $ = _c(9);
  const {
    gettext
  } = useI18n();
  const {
    status,
    data: recommendationsSections
  } = useRecommendationsSections();
  const recommendations = status === LoadingStatus.LOADED ? recommendationsSections[0] : undefined;
  let t0;
  if ($[0] !== gettext || $[1] !== recommendations?.title) {
    t0 = recommendations?.title ?? gettext("Students also bought");
    $[0] = gettext;
    $[1] = recommendations?.title;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const title = t0;
  let t1;
  bb0: {
    if (!recommendations) {
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t1 = t2;
      break bb0;
    }
    let courses;
    if ($[4] !== recommendations) {
      courses = [];
      if ("items" in recommendations) {
        recommendations.items.forEach(item => {
          if (item && typeof item === "object" && "course" in item && item.course) {
            courses.push({
              ...(item.course as BrowseCourse),
              trackingId: item.trackingId,
              frontendTrackingId: item.frontendTrackingId
            });
          }
        });
      }
      $[4] = recommendations;
      $[5] = courses;
    } else {
      courses = $[5];
    }
    t1 = courses;
  }
  const courseRecommendations = t1;
  if (status !== LoadingStatus.LOADED) {
    return null;
  }
  let t2;
  if ($[6] !== courseRecommendations || $[7] !== title) {
    t2 = <StudentsAlsoBought courses={courseRecommendations} title={title} />;
    $[6] = courseRecommendations;
    $[7] = title;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};